import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Megaphone, Pin, AlertTriangle, Send, Search, Plus, ArrowLeft, X,
  MessageCircle, Globe, ImagePlus, Trash2, Pen, ChevronDown, Users,
} from 'lucide-react';
import { format, parseISO, isToday, isYesterday } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Unified iPhone-Messages-style inbox.
 * Single chronological list of Announcements + Direct Message conversations.
 * Tap announcement -> /messages/announcements/:id (existing route preserved)
 * Tap DM -> opens inline thread pane (right on desktop, full-screen on mobile)
 * "New" button -> dropdown: New Message / New Announcement (admins/coaches only)
 *
 * STRICT SCOPE: this component only orchestrates the inbox UI.
 * No backend / API contract / model / permission changes.
 */
const MessagesContent = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Data
  const [announcements, setAnnouncements] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // DM thread state
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // New conversation state
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [userDirectory, setUserDirectory] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  // Aug 2026 — multi-recipient DM support (frontend-only; backend
  // `POST /api/dm/conversations` already accepts recipient_ids arrays
  // of any length). `selectedRecipients` is the source of truth; the
  // legacy `selectedRecipient` state is kept only to feed the deep-link
  // `?dm_user=<uid>` prefill path without breaking that flow.
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [initialMessage, setInitialMessage] = useState('');
  const [recipientSearch, setRecipientSearch] = useState('');

  // Compose announcement state
  const [showComposeAnnouncement, setShowComposeAnnouncement] = useState(false);
  const [composeData, setComposeData] = useState({
    title: '', body: '', is_public: false, flyer_url: '', flyer_file_key: '',
    // Twilio SMS MVP (Feb 2026) — additive composer state. Defaults preserve
    // legacy in-app-only behavior. SMS branch fires only when 'sms' is
    // included in channels[].
    channels: ['in_app'],
    sms_body_override: '',
    urgent: false,
    override_reason: '',
    include_volunteers: false, // reserved for future event-scoped SMS composer
  });
  const [smsPreview, setSmsPreview] = useState(null); // {eligible, opted_out, invalid_phone, ...}
  const [smsPreviewLoading, setSmsPreviewLoading] = useState(false);
  const [composeFlyer, setComposeFlyer] = useState(null);
  const [uploadingFlyer, setUploadingFlyer] = useState(false);
  const [composeSending, setComposeSending] = useState(false);

  const messagesEndRef = useRef(null);

  const isAdmin = user?.role === 'league_admin' || user?.role === 'super_admin';
  const isCoach = user?.role === 'coach' || user?.role === 'team_staff';
  // Announcement creation is admin-only (league_admin / super_admin).
  // Coaches, team_staff, team_mom, and parents do not see the
  // "New Announcement" affordance and backend POST /api/announcements
  // returns 403 for them as well.
  const canPostAnnouncement = isAdmin;

  useEffect(() => {
    fetchAll();
    const recipientId = searchParams.get('dm_user');
    if (recipientId) {
      fetchUserAndStartConversation(recipientId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchAll = async () => {
    try {
      const [annRes, convRes] = await Promise.all([
        axios.get(`${API_URL}/api/announcements`, { withCredentials: true }),
        axios.get(`${API_URL}/api/dm/conversations`, { withCredentials: true }),
      ]);
      setAnnouncements(annRes.data || []);
      setConversations(convRes.data || []);
    } catch (error) {
      console.error('Failed to fetch inbox:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserAndStartConversation = async (userId) => {
    try {
      const res = await axios.get(`${API_URL}/api/dm/users?search=`, { withCredentials: true });
      const targetUser = res.data.find(u => u.user_id === userId);
      if (targetUser) {
        setSelectedRecipient(targetUser);
        setSelectedRecipients([targetUser]);
        setShowNewConversation(true);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const res = await axios.get(`${API_URL}/api/dm/conversations/${conversationId}`, { withCredentials: true });
      setMessages(res.data.messages || []);
      setParticipants(res.data.participants_info || res.data.participants || []);
      setConversations(prev => prev.map(c =>
        c.conversation_id === conversationId ? { ...c, unread_count: 0 } : c
      ));
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const fetchUserDirectory = async (q = '') => {
    try {
      const res = await axios.get(`${API_URL}/api/dm/users?search=${encodeURIComponent(q)}`, { withCredentials: true });
      setUserDirectory(res.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  // ---------- Inbox row helpers ----------
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getOtherParticipants = (conv) =>
    conv.participants_info?.filter(p => p.user_id !== user?.user_id) || [];

  const getConversationName = (conv) => {
    // Aug 2026 — team huddles carry their own title rather than a
    // participant-derived name (participants are resolved dynamically).
    if (conv?.conversation_type === 'team_huddle') {
      return conv.title || 'Team Huddle';
    }
    const others = getOtherParticipants(conv);
    if (others.length === 0) return 'Unknown';
    if (others.length === 1) return others[0].name;
    return others.map(p => p.name?.split(' ')[0] || '').join(', ');
  };

  const formatRowTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = parseISO(dateStr);
      if (isToday(date)) return format(date, 'h:mm a');
      if (isYesterday(date)) return 'Yesterday';
      return format(date, 'MMM d');
    } catch { return ''; }
  };

  const formatThreadTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = parseISO(dateStr);
      if (isToday(date)) return format(date, 'h:mm a');
      if (isYesterday(date)) return 'Yesterday ' + format(date, 'h:mm a');
      return format(date, 'MMM d, h:mm a');
    } catch { return ''; }
  };

  // ---------- Build unified rows ----------
  const announcementRows = announcements.map(a => ({
    type: 'announcement',
    key: `ann-${a.announcement_id}`,
    id: a.announcement_id,
    title: a.title,
    preview: a.body,
    sender: a.sender_name,
    time: a.published_at || a.created_at,
    is_unread: !a.is_read,
    is_pinned: a.is_pinned,
    is_critical: a.is_critical,
    raw: a,
  }));

  const conversationRows = conversations.map(c => ({
    type: 'dm',
    // Aug 2026 — huddles reuse the DM inbox row but render as a
    // separate "Team Huddles" section header above regular messages.
    is_huddle: c.conversation_type === 'team_huddle',
    key: `dm-${c.conversation_id}`,
    id: c.conversation_id,
    title: getConversationName(c),
    preview: c.last_message_preview || c.last_message || '',
    sender: null,
    time: c.last_message_at,
    is_unread: (c.unread_count || 0) > 0,
    unread_count: c.unread_count || 0,
    is_pinned: false,
    is_critical: false,
    raw: c,
  }));

  const _matchesSearch = (r) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      (r.title || '').toLowerCase().includes(q) ||
      (r.preview || '').toLowerCase().includes(q)
    );
  };
  const _sortByRecency = (a, b) => {
    if (a.is_pinned && !b.is_pinned) return -1;
    if (!a.is_pinned && b.is_pinned) return 1;
    const ta = a.time ? new Date(a.time).getTime() : 0;
    const tb = b.time ? new Date(b.time).getTime() : 0;
    return tb - ta;
  };

  const huddleRows = conversationRows
    .filter(r => r.is_huddle)
    .filter(_matchesSearch)
    .sort(_sortByRecency);

  const allRows = [
    ...announcementRows,
    ...conversationRows.filter(r => !r.is_huddle),
  ]
    .filter(_matchesSearch)
    .sort(_sortByRecency);

  const totalUnread =
    announcementRows.filter(r => r.is_unread).length +
    conversationRows.reduce((sum, r) => sum + (r.unread_count || 0), 0);

  // ---------- Row actions ----------
  const handleRowClick = (row) => {
    if (row.type === 'announcement') {
      navigate(`/messages/announcements/${row.id}`);
    } else {
      setSelectedConversation(row.raw);
      setShowNewConversation(false);
      fetchMessages(row.id);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    setSendingMessage(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/dm/conversations/${selectedConversation.conversation_id}/messages`,
        { body: newMessage },
        { withCredentials: true }
      );
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
      setConversations(prev => prev.map(c =>
        c.conversation_id === selectedConversation.conversation_id
          ? { ...c, last_message_preview: newMessage.substring(0, 100), last_message_at: res.data.sent_at }
          : c
      ).sort((a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0)));
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleStartNewConversation = async () => {
    const recipients = selectedRecipients.length > 0
      ? selectedRecipients
      : (selectedRecipient ? [selectedRecipient] : []);
    if (recipients.length === 0 || !initialMessage.trim()) return;
    setSendingMessage(true);
    try {
      const recipientIds = recipients.map(r => r.user_id);
      const res = await axios.post(
        `${API_URL}/api/dm/conversations`,
        {
          recipient_ids: recipientIds,
          participant_ids: recipientIds,
          initial_message: initialMessage,
        },
        { withCredentials: true }
      );
      const conv = res.data;
      const refreshed = await axios.get(`${API_URL}/api/dm/conversations`, { withCredentials: true });
      setConversations(refreshed.data || []);
      setSelectedConversation(conv);
      fetchMessages(conv.conversation_id);
      setShowNewConversation(false);
      setSelectedRecipient(null);
      setSelectedRecipients([]);
      setInitialMessage('');
      setRecipientSearch('');
    } catch (error) {
      console.error('Failed to start conversation:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleOpenNewConversation = () => {
    setShowNewConversation(true);
    setSelectedConversation(null);
    setSelectedRecipient(null);
    setSelectedRecipients([]);
    setInitialMessage('');
    setRecipientSearch('');
    fetchUserDirectory();
  };

  const handleToggleRecipient = (u) => {
    // Prevent adding self (backend adds sender automatically).
    if (u.user_id === user?.user_id) return;
    setSelectedRecipient(u); // legacy compatibility
    setSelectedRecipients(prev => {
      if (prev.some(p => p.user_id === u.user_id)) return prev;
      return [...prev, u];
    });
    setRecipientSearch('');
    // Refresh directory so the search input clears + shows the full list again.
    fetchUserDirectory('');
  };

  const handleRemoveRecipient = (userId) => {
    setSelectedRecipients(prev => {
      const next = prev.filter(p => p.user_id !== userId);
      // Keep the legacy single-recipient state aligned so the composer
      // continues working when the array is empty or has just one entry.
      setSelectedRecipient(next[0] || null);
      return next;
    });
  };

  const handleSearchUsers = (term) => {
    setRecipientSearch(term);
    fetchUserDirectory(term);
  };

  // ---------- Compose announcement ----------
  const handleFlyerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('Image must be under 5MB'); return; }
    setUploadingFlyer(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        setComposeFlyer(reader.result);
        const res = await axios.post(`${API_URL}/api/events/upload-image`, {
          file_data: reader.result, filename: file.name, content_type: file.type,
        }, { withCredentials: true });
        setComposeData(prev => ({ ...prev, flyer_url: res.data.image_url, flyer_file_key: res.data.file_key }));
        setUploadingFlyer(false);
      };
      reader.readAsDataURL(file);
    } catch { setUploadingFlyer(false); }
  };

  const isSuperOrLeagueAdmin = user?.role === 'super_admin' || user?.role === 'league_admin';
  const wantsSms = composeData.channels.includes('sms');

  // Char / segment count for the SMS composer live preview.
  const smsPayloadBody = (composeData.sms_body_override || composeData.body || '').trim();
  const smsCharCount = smsPayloadBody.length;
  const smsSegmentCount = smsCharCount === 0
    ? 0
    : smsCharCount <= 160 ? 1 : Math.ceil(smsCharCount / 153);

  const toggleChannel = (ch) => {
    setComposeData((prev) => {
      const set = new Set(prev.channels);
      if (set.has(ch)) set.delete(ch); else set.add(ch);
      // in_app is the persistent baseline — always include so the
      // announcement itself gets persisted in the inbox.
      set.add('in_app');
      return { ...prev, channels: Array.from(set) };
    });
    setSmsPreview(null);
  };

  const fetchSmsPreview = async () => {
    if (!wantsSms) return;
    setSmsPreviewLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/sms/preview`,
        {
          recipient_user_ids: [],
          urgent: !!composeData.urgent,
          body: smsPayloadBody,
        },
        { withCredentials: true },
      );
      setSmsPreview(res.data);
    } catch (e) {
      setSmsPreview({ error: e.response?.data?.detail || 'Preview failed' });
    } finally {
      setSmsPreviewLoading(false);
    }
  };

  const handleCreateAnnouncement = async () => {
    if (!composeData.title.trim() || !composeData.body.trim()) return;
    if (wantsSms && composeData.urgent && !composeData.override_reason.trim()) {
      alert('Please provide a reason for the urgent quiet-hours override.');
      return;
    }
    setComposeSending(true);
    try {
      const payload = {
        title: composeData.title,
        body: composeData.body,
        is_public: composeData.is_public,
        flyer_url: composeData.flyer_url || null,
        flyer_file_key: composeData.flyer_file_key || null,
        audience_type: 'all_users',
        priority: 'normal',
        channels: composeData.channels,
      };
      if (wantsSms) {
        if (composeData.sms_body_override.trim()) {
          payload.sms_body_override = composeData.sms_body_override.trim();
        }
        if (composeData.urgent) {
          payload.urgent = true;
          payload.override_reason = composeData.override_reason.trim();
        }
      }
      const res = await axios.post(`${API_URL}/api/announcements`, payload, { withCredentials: true });
      setShowComposeAnnouncement(false);
      setComposeData({
        title: '', body: '', is_public: false, flyer_url: '', flyer_file_key: '',
        channels: ['in_app'], sms_body_override: '', urgent: false, override_reason: '',
        include_volunteers: false,
      });
      setComposeFlyer(null);
      setSmsPreview(null);
      const sms = res?.data?.sms;
      if (sms && wantsSms) {
        if (sms.enabled) {
          alert(`Announcement posted. SMS: sent=${sms.sent}, skipped=${sms.skipped}, errors=${sms.errors}.`);
        } else {
          alert(`Announcement posted. SMS service is currently disabled (${sms.reason || 'kill switch off'}); no text messages were sent.`);
        }
      }
      fetchAll();
    } catch (error) {
      console.error('Failed to create announcement:', error);
      alert(error.response?.data?.detail || 'Failed to create announcement');
    } finally {
      setComposeSending(false);
    }
  };

  // ---------- Render ----------
  if (loading) {
    return (
      <div className="animate-pulse space-y-4" data-testid="messages-content-loading">
        <div className="h-10 bg-gray-200 rounded-lg w-1/3" />
        <div className="h-64 bg-gray-200 rounded-lg" />
      </div>
    );
  }

  // Detail / compose pane content
  const detailPaneOpen = !!selectedConversation || showNewConversation;

  return (
    <div data-testid="messages-content">
      {/* Header bar: title + unread pill + New button */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg sm:text-xl font-bold text-navy" data-testid="inbox-title">Inbox</h2>
          {totalUnread > 0 && (
            <Badge className="bg-red text-white text-xs" data-testid="inbox-unread-pill">
              {totalUnread} unread
            </Badge>
          )}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              className="bg-navy hover:bg-navy/90 text-white"
              data-testid="inbox-new-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New
              <ChevronDown className="w-3 h-3 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={handleOpenNewConversation}
              data-testid="inbox-new-message"
            >
              <Pen className="w-4 h-4 mr-2" />
              New Message
            </DropdownMenuItem>
            {canPostAnnouncement && (
              <DropdownMenuItem
                onClick={() => { setShowComposeAnnouncement(true); setSelectedConversation(null); setShowNewConversation(false); }}
                data-testid="inbox-new-announcement"
              >
                <Megaphone className="w-4 h-4 mr-2" />
                New Announcement
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Compose Announcement (slide-over style — kept inline like before but visually contained) */}
      {showComposeAnnouncement && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4 shadow-sm" data-testid="compose-announcement-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-navy text-lg">New Announcement</h3>
            <button
              onClick={() => { setShowComposeAnnouncement(false); setComposeFlyer(null); }}
              className="text-gray-400 hover:text-gray-600"
              data-testid="compose-announcement-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-4">
            <input
              type="text"
              placeholder="Announcement title..."
              value={composeData.title}
              onChange={(e) => setComposeData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent text-base font-medium"
              data-testid="compose-title-input"
            />
            <textarea
              placeholder="Write your announcement..."
              value={composeData.body}
              onChange={(e) => setComposeData(prev => ({ ...prev, body: e.target.value }))}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
              data-testid="compose-body-input"
            />

            {composeFlyer ? (
              <div className="relative rounded-lg overflow-hidden border border-gray-200">
                <img src={composeFlyer} alt="Flyer" className="w-full max-h-48 object-contain bg-gray-50" />
                <button
                  onClick={() => { setComposeFlyer(null); setComposeData(prev => ({ ...prev, flyer_url: '', flyer_file_key: '' })); }}
                  className="absolute top-2 right-2 bg-red text-white p-1.5 rounded-full hover:bg-red/80"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <label className={`flex items-center gap-3 px-4 py-3 border border-dashed rounded-lg cursor-pointer transition ${uploadingFlyer ? 'border-gold bg-gold/5' : 'border-gray-300 hover:border-navy'}`}>
                <input type="file" accept="image/*" onChange={handleFlyerUpload} className="hidden" disabled={uploadingFlyer} />
                {uploadingFlyer ? (
                  <div className="w-5 h-5 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                ) : (
                  <ImagePlus className="w-5 h-5 text-gray-400" />
                )}
                <span className="text-sm text-gray-500">{uploadingFlyer ? 'Uploading...' : 'Add a flyer or image (optional)'}</span>
              </label>
            )}

            {isAdmin && (
              <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition ${composeData.is_public ? 'border-gold bg-gold/10' : 'border-gray-200 hover:border-gray-300'}`} data-testid="compose-public-toggle">
                <input
                  type="checkbox"
                  checked={composeData.is_public}
                  onChange={(e) => setComposeData(prev => ({ ...prev, is_public: e.target.checked }))}
                  className="w-5 h-5 rounded border-gray-300 text-gold focus:ring-gold"
                />
                <Globe className="w-4 h-4 text-gold" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Show on Public Guest Page</p>
                  <p className="text-xs text-gray-500">Visible to visitors without login (max 2 shown)</p>
                </div>
              </label>
            )}

            {/* Twilio SMS MVP (Feb 2026) — channels picker + SMS composer.
                In-app is always on. SMS branch is default-off; when SMS is
                selected the composer surfaces a body override + live segment
                count + preview + optional urgent quiet-hours override
                (super_admin / league_admin only). Kill switch (TWILIO_ENABLED)
                stays authoritative on the backend — clicking Send never
                fans out live traffic until ops flip the switch. */}
            {isAdmin && (
              <div className="rounded-lg border-2 border-gray-200 p-3 space-y-3" data-testid="compose-channels-block">
                <p className="text-sm font-semibold text-navy">Deliver via</p>
                <div className="flex flex-wrap gap-2 text-sm">
                  <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 text-gray-700 cursor-not-allowed">
                    <input type="checkbox" checked disabled className="w-4 h-4" />
                    In-app (always on)
                  </label>
                  <label
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full cursor-pointer transition ${composeData.channels.includes('sms') ? 'bg-navy text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    data-testid="compose-channel-sms"
                  >
                    <input
                      type="checkbox"
                      checked={composeData.channels.includes('sms')}
                      onChange={() => toggleChannel('sms')}
                      className="w-4 h-4"
                    />
                    SMS
                  </label>
                </div>

                {wantsSms && (
                  <div className="space-y-3 border-t pt-3" data-testid="compose-sms-composer">
                    <div>
                      <label className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                        SMS message (leaves the in-app body untouched)
                      </label>
                      <textarea
                        placeholder="Overrides the announcement body when sending as SMS. Leave blank to reuse the in-app text."
                        value={composeData.sms_body_override}
                        onChange={(e) => { setComposeData(prev => ({ ...prev, sms_body_override: e.target.value })); setSmsPreview(null); }}
                        rows={2}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        data-testid="compose-sms-body"
                      />
                      <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
                        <span data-testid="compose-sms-char-count">
                          {smsCharCount} char{smsCharCount === 1 ? '' : 's'} · {smsSegmentCount} segment{smsSegmentCount === 1 ? '' : 's'}
                        </span>
                        {smsSegmentCount > 1 && (
                          <span className="text-yellow-700">Message will send as {smsSegmentCount} segments (each billable)</span>
                        )}
                      </div>
                    </div>

                    {isSuperOrLeagueAdmin && (
                      <label
                        className={`flex items-start gap-3 p-2.5 rounded-lg border cursor-pointer transition ${composeData.urgent ? 'border-red bg-red/5' : 'border-gray-200'}`}
                        data-testid="compose-urgent-toggle"
                      >
                        <input
                          type="checkbox"
                          checked={composeData.urgent}
                          onChange={(e) => setComposeData(prev => ({ ...prev, urgent: e.target.checked, override_reason: e.target.checked ? prev.override_reason : '' }))}
                          className="w-4 h-4 mt-0.5 rounded"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-navy">Urgent — bypass quiet hours</p>
                          <p className="text-xs text-gray-500">Only for cancellations, safety notices, or emergency location changes. Requires a reason.</p>
                          {composeData.urgent && (
                            <input
                              type="text"
                              placeholder="Override reason (required)"
                              value={composeData.override_reason}
                              onChange={(e) => setComposeData(prev => ({ ...prev, override_reason: e.target.value }))}
                              className="mt-2 w-full px-3 py-1.5 border border-red/40 rounded text-sm"
                              data-testid="compose-urgent-reason"
                            />
                          )}
                        </div>
                      </label>
                    )}

                    <div className="flex flex-wrap items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={fetchSmsPreview}
                        disabled={smsPreviewLoading || !smsPayloadBody}
                        data-testid="compose-sms-preview-btn"
                      >
                        {smsPreviewLoading ? 'Loading...' : 'Preview audience'}
                      </Button>
                      {smsPreview && !smsPreview.error && (
                        <div className="text-xs text-gray-600 flex flex-wrap gap-x-3 gap-y-1" data-testid="compose-sms-preview-counts">
                          <span>Eligible: <strong className="text-navy">{smsPreview.eligible}</strong></span>
                          <span>Opted-out: {smsPreview.opted_out}</span>
                          <span>Invalid phone: {smsPreview.invalid_phone}</span>
                          <span>Deduped: {smsPreview.duplicates_removed}</span>
                          {smsPreview.quiet_hours_would_block && !composeData.urgent && (
                            <span className="text-yellow-700">Quiet hours block — override required</span>
                          )}
                          {!smsPreview.sms_service_enabled && (
                            <span className="text-red">SMS service currently disabled (no live sends)</span>
                          )}
                        </div>
                      )}
                      {smsPreview?.error && (
                        <span className="text-xs text-red" data-testid="compose-sms-preview-error">{smsPreview.error}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => { setShowComposeAnnouncement(false); setComposeFlyer(null); }}>Cancel</Button>
              <Button
                className="bg-red hover:bg-red/90 text-white"
                disabled={!composeData.title.trim() || !composeData.body.trim() || composeSending || uploadingFlyer}
                onClick={handleCreateAnnouncement}
                data-testid="compose-send-btn"
              >
                {composeSending ? 'Sending...' : 'Send Announcement'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Search messages..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="inbox-search"
        />
      </div>

      {/* Two-pane layout: Inbox list (left) + Detail (right on desktop, full-screen overlay on mobile) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" style={{ minHeight: '500px' }}>
        <div className="flex" style={{ minHeight: '500px' }}>
          {/* LEFT: Unified Inbox list */}
          <div
            className={`w-full md:w-2/5 border-r border-gray-200 flex flex-col ${detailPaneOpen ? 'hidden md:flex' : 'flex'}`}
            data-testid="inbox-list-pane"
          >
            <div className="flex-1 overflow-y-auto">
              {huddleRows.length > 0 && (
                <>
                  <div
                    className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-[10px] font-bold uppercase tracking-wider text-gray-600"
                    data-testid="huddle-section-header"
                  >
                    Team Huddles
                  </div>
                  {huddleRows.map(row => (
                    <button
                      key={row.key}
                      type="button"
                      onClick={() => handleRowClick(row)}
                      className={`w-full text-left p-3 border-b border-gray-100 transition-colors ${
                        selectedConversation?.conversation_id === row.id
                          ? 'bg-navy/5 border-l-4 border-l-navy'
                          : 'hover:bg-gray-50'
                      } ${row.is_unread ? 'bg-red/5' : ''}`}
                      data-testid={`inbox-row-huddle-${row.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold to-yellow-500 flex items-center justify-center text-navy font-bold text-xs flex-shrink-0">
                          <Users className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className={`text-sm truncate text-navy ${row.is_unread ? 'font-bold' : 'font-semibold'}`}>
                              {row.title}
                            </p>
                            <span className="text-[10px] text-gray-400 flex-shrink-0">
                              {formatRowTime(row.time)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                              Team Huddle
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 truncate mt-0.5">
                            {row.preview || 'Start the conversation with your team.'}
                          </p>
                        </div>
                        {row.is_unread && row.unread_count > 0 && (
                          <Badge className="bg-red text-white text-[10px] flex-shrink-0">{row.unread_count}</Badge>
                        )}
                      </div>
                    </button>
                  ))}
                  {allRows.length > 0 && (
                    <div
                      className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-[10px] font-bold uppercase tracking-wider text-gray-600"
                      data-testid="messages-section-header"
                    >
                      Messages
                    </div>
                  )}
                </>
              )}
              {allRows.length > 0 ? (
                allRows.map(row => (
                  <button
                    key={row.key}
                    type="button"
                    onClick={() => handleRowClick(row)}
                    className={`w-full text-left p-3 border-b border-gray-100 transition-colors ${
                      row.type === 'dm' && selectedConversation?.conversation_id === row.id
                        ? 'bg-navy/5 border-l-4 border-l-navy'
                        : 'hover:bg-gray-50'
                    } ${row.is_unread ? 'bg-red/5' : ''}`}
                    data-testid={`inbox-row-${row.type}-${row.id}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Leading icon / avatar */}
                      {row.type === 'announcement' ? (
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          row.is_critical ? 'bg-red/10 text-red' : row.is_pinned ? 'bg-gold/20 text-yellow-700' : 'bg-navy/10 text-navy'
                        }`}>
                          {row.is_critical ? <AlertTriangle className="w-5 h-5" /> : row.is_pinned ? <Pin className="w-5 h-5" /> : <Megaphone className="w-5 h-5" />}
                        </div>
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-navy to-light-blue flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                          {(() => {
                            const others = getOtherParticipants(row.raw);
                            if (others.length === 1 && others[0].picture) {
                              return <img src={others[0].picture} alt="" className="w-full h-full rounded-full object-cover" />;
                            }
                            return getInitials(row.title);
                          })()}
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className={`text-sm truncate text-navy ${row.is_unread ? 'font-bold' : 'font-semibold'}`}>
                            {row.title}
                          </p>
                          <span className="text-[10px] text-gray-400 flex-shrink-0">
                            {formatRowTime(row.time)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                          {row.type === 'announcement' && (
                            <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                              {row.is_critical ? 'Critical' : row.is_pinned ? 'Pinned' : 'Announcement'}
                            </span>
                          )}
                          {row.type === 'dm' && (
                            <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                              Message
                            </span>
                          )}
                          {row.type === 'announcement' && row.sender && (
                            <>
                              <span className="text-[10px] text-gray-300">•</span>
                              <span className="text-[10px] text-gray-500 truncate">{row.sender}</span>
                            </>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {row.preview}
                        </p>
                      </div>

                      {row.is_unread && row.type === 'dm' && row.unread_count > 0 && (
                        <Badge className="bg-red text-white text-[10px] flex-shrink-0">{row.unread_count}</Badge>
                      )}
                      {row.is_unread && row.type === 'announcement' && (
                        <span className="w-2 h-2 bg-red rounded-full flex-shrink-0 mt-2" />
                      )}
                    </div>
                  </button>
                ))
              ) : huddleRows.length === 0 ? (
                <div className="p-8 text-center" data-testid="inbox-empty-state">
                  <MessageCircle className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500 text-sm font-medium">Your inbox is empty</p>
                  <p className="text-gray-400 text-xs mt-1">Tap "New" to send a message{canPostAnnouncement ? ' or post an announcement' : ''}.</p>
                </div>
              ) : null}
            </div>
          </div>

          {/* RIGHT: Detail pane (DM thread / new conversation / empty state) */}
          <div className={`flex-1 flex flex-col ${!detailPaneOpen ? 'hidden md:flex' : 'flex'}`} data-testid="inbox-detail-pane">
            {showNewConversation ? (
              /* New Conversation View */
              <div className="flex flex-col h-full">
                <div className="p-3 border-b border-gray-200 flex items-center space-x-3">
                  <button
                    onClick={() => { setShowNewConversation(false); setSelectedRecipient(null); setSelectedRecipients([]); }}
                    className="p-1.5 hover:bg-gray-100 rounded-lg"
                    data-testid="new-conv-back"
                  >
                    <ArrowLeft className="w-5 h-5 text-gray-600" />
                  </button>
                  <h2 className="font-bold text-navy uppercase text-sm">New Message</h2>
                </div>

                <div className="p-3 border-b border-gray-200">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2 block">To:</label>
                  <div
                    className="flex flex-wrap gap-2 mb-2"
                    data-testid="dm-recipient-chips"
                  >
                    {selectedRecipients.map(r => (
                      <span
                        key={r.user_id}
                        className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-full bg-navy/10 border border-navy/20 text-xs text-navy"
                        data-testid={`dm-recipient-chip-${r.user_id}`}
                      >
                        <span className="font-medium truncate max-w-[10rem]">{r.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveRecipient(r.user_id)}
                          className="p-0.5 rounded-full hover:bg-navy/20"
                          data-testid={`dm-recipient-chip-remove-${r.user_id}`}
                          aria-label={`Remove ${r.name}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder={selectedRecipients.length === 0 ? "Search for a person..." : "Search another person..."}
                      value={recipientSearch}
                      onChange={(e) => handleSearchUsers(e.target.value)}
                      className="pl-10"
                      data-testid="dm-user-search"
                    />
                  </div>
                </div>

                {selectedRecipients.length === 0 ? (
                  <div className="flex-1 overflow-y-auto p-3">
                    <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Select a recipient</p>
                    <div className="space-y-1">
                      {userDirectory
                        .filter(u => !selectedRecipients.some(r => r.user_id === u.user_id))
                        .map(u => (
                          <button
                            key={u.user_id}
                            type="button"
                            onClick={() => handleToggleRecipient(u)}
                            className="w-full flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer border border-gray-100 transition-colors text-left"
                            data-testid={`dm-user-${u.user_id}`}
                          >
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-navy to-light-blue flex items-center justify-center text-white text-xs font-bold">
                              {getInitials(u.name)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-navy text-sm truncate">{u.name}</p>
                              <p className="text-xs text-gray-500 truncate">{u.email}</p>
                            </div>
                            {u.role && (
                              <Badge variant="outline" className="text-[10px]">
                                {u.role.replace('_', ' ')}
                              </Badge>
                            )}
                          </button>
                        ))}
                      {userDirectory.length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-4">No users found.</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Add-more-people directory: only shows results when the
                        user is actively searching, or a small pinned "top
                        results" list otherwise. */}
                    {recipientSearch && userDirectory.length > 0 && (
                      <div className="border-b border-gray-200 max-h-48 overflow-y-auto" data-testid="dm-add-more-list">
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-500 px-3 pt-3">Add another</p>
                        <div className="p-2 space-y-1">
                          {userDirectory
                            .filter(u => !selectedRecipients.some(r => r.user_id === u.user_id))
                            .map(u => (
                              <button
                                key={u.user_id}
                                type="button"
                                onClick={() => handleToggleRecipient(u)}
                                className="w-full flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer border border-gray-100 transition-colors text-left"
                                data-testid={`dm-user-${u.user_id}`}
                              >
                                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-navy to-light-blue flex items-center justify-center text-white text-[10px] font-bold">
                                  {getInitials(u.name)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-navy text-sm truncate">{u.name}</p>
                                  <p className="text-xs text-gray-500 truncate">{u.email}</p>
                                </div>
                                {u.role && (
                                  <Badge variant="outline" className="text-[10px]">
                                    {u.role.replace('_', ' ')}
                                  </Badge>
                                )}
                              </button>
                            ))}
                        </div>
                      </div>
                    )}
                    <div className="flex-1 p-4 flex items-center justify-center">
                      <p className="text-gray-500 text-sm text-center">
                        {selectedRecipients.length === 1
                          ? `Start the conversation with ${selectedRecipients[0].name}`
                          : `New group message with ${selectedRecipients.map(r => r.name.split(' ')[0]).join(', ')}`}
                      </p>
                    </div>
                    <div className="p-3 border-t border-gray-200">
                      <div className="flex space-x-2">
                        <Input
                          placeholder="Type your message..."
                          value={initialMessage}
                          onChange={(e) => setInitialMessage(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleStartNewConversation()}
                          className="flex-1"
                          data-testid="dm-initial-msg"
                        />
                        <Button
                          onClick={handleStartNewConversation}
                          disabled={!initialMessage.trim() || sendingMessage || selectedRecipients.length === 0}
                          className="bg-red hover:bg-red/90 text-white"
                          data-testid="dm-send-initial"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : selectedConversation ? (
              /* Active Conversation View */
              <div className="flex flex-col h-full">
                <div className="p-3 border-b border-gray-200 flex items-center space-x-3">
                  <button
                    onClick={() => setSelectedConversation(null)}
                    className="p-1.5 hover:bg-gray-100 rounded-lg md:hidden"
                    data-testid="conv-back-btn"
                  >
                    <ArrowLeft className="w-5 h-5 text-gray-600" />
                  </button>
                  {selectedConversation.conversation_type === 'team_huddle' ? (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gold to-yellow-500 flex items-center justify-center text-navy text-xs font-bold">
                      <Users className="w-4 h-4" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-navy to-light-blue flex items-center justify-center text-white text-xs font-bold">
                      {getInitials(
                        participants.filter(p => p.user_id !== user?.user_id)[0]?.name ||
                        getConversationName(selectedConversation)
                      )}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-navy text-sm truncate">
                      {selectedConversation.conversation_type === 'team_huddle'
                        ? getConversationName(selectedConversation)
                        : (participants.filter(p => p.user_id !== user?.user_id).map(p => p.name).join(', ') ||
                           getConversationName(selectedConversation))}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {selectedConversation.conversation_type === 'team_huddle'
                        ? `${participants.length} member${participants.length === 1 ? '' : 's'}`
                        : participants.filter(p => p.user_id !== user?.user_id).map(p => p.role?.replace('_', ' ')).filter(Boolean).join(', ')}
                    </p>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.map((msg, idx) => {
                    const isMe = msg.sender_id === user?.user_id;
                    const showAvatar = idx === 0 || messages[idx - 1]?.sender_id !== msg.sender_id;
                    return (
                      <div key={msg.message_id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                        <div className={`flex items-end space-x-2 max-w-[80%] ${isMe ? 'flex-row-reverse space-x-reverse' : ''}`}>
                          {showAvatar && !isMe && (
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-navy to-light-blue flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                              {getInitials(msg.sender_name)}
                            </div>
                          )}
                          {!showAvatar && !isMe && <div className="w-7" />}

                          <div className={`rounded-2xl px-3 py-2 ${
                            isMe ? 'bg-navy text-white rounded-br-md' : 'bg-gray-100 text-gray-800 rounded-bl-md'
                          }`}>
                            {showAvatar && !isMe && (
                              <p className="text-xs font-semibold mb-1 text-navy">{msg.sender_name}</p>
                            )}
                            <p className="text-sm whitespace-pre-wrap">{msg.body || msg.content}</p>
                            <p className={`text-[10px] mt-1 ${isMe ? 'text-white/70' : 'text-gray-400'}`}>
                              {formatThreadTime(msg.sent_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                <div className="p-3 border-t border-gray-200">
                  <div className="flex space-x-2">
                    <Input
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      className="flex-1"
                      data-testid="dm-msg-input"
                    />
                    <Button
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim() || sendingMessage}
                      className="bg-red hover:bg-red/90 text-white"
                      data-testid="dm-send-msg"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              /* Empty State */
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageCircle className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500 font-medium text-sm">Select a message</p>
                  <p className="text-gray-400 text-xs mt-1">Or tap "New" to start one</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesContent;
